import React, { useEffect, createElement } from 'react';
import { MotionConfig } from 'framer-motion';
import { Landing } from './pages/Landing';
import { useScreenInit } from './useScreenInit';
const META_PIXEL_ID = '1594689019333970';
const PAGE_TITLE = 'MASTERPACK DE PROTOCOLOS CLÍNICOS DE NUTRICIÓN';
const PAGE_DESC = 'El ecosistema completo de protocolos nutricionales para que tus pacientes logren resultados reales, sin que vos pierdas horas diseñando material desde cero.';
const HERO_IMAGE = 'https://cdn.magicpatterns.com/uploads/jZffEDPzmHH2USTTout28h/Texto_del_parrafo_(10)_(1).webp';
const SITE_URL = 'https://masterpack-nutricion.com';
function setMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}
function addLink(rel: string, href: string, attrs: Record<string, string> = {}) {
  const selector = `link[rel="${rel}"][href="${href}"]`;
  if (document.head.querySelector(selector)) return;
  const el = document.createElement('link');
  el.rel = rel;
  el.href = href;
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  document.head.appendChild(el);
}
export function App() {
  useScreenInit();
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.lang = 'es-AR';
    document.title = PAGE_TITLE;
    addLink('preconnect', 'https://cdn.magicpatterns.com', {
      crossorigin: ''
    });
    addLink('preconnect', 'https://fonts.googleapis.com');
    addLink('preconnect', 'https://fonts.gstatic.com', {
      crossorigin: ''
    });
    addLink('preload', HERO_IMAGE, {
      as: 'image',
      fetchpriority: 'high'
    });
    addLink('canonical', SITE_URL);
    setMeta('name', 'description', PAGE_DESC);
    setMeta('name', 'viewport', 'width=device-width, initial-scale=1, viewport-fit=cover');
    setMeta('name', 'theme-color', '#064e3b');
    setMeta('name', 'robots', 'index, follow');
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:title', PAGE_TITLE);
    setMeta('property', 'og:description', PAGE_DESC);
    setMeta('property', 'og:locale', 'es_AR');
    setMeta('property', 'og:image', HERO_IMAGE);
    setMeta('property', 'og:url', SITE_URL);
    setMeta('property', 'og:site_name', 'Masterpack Nutrición');
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:image', HERO_IMAGE);
    setMeta('name', 'twitter:title', PAGE_TITLE);
    setMeta('name', 'twitter:description', PAGE_DESC);
    const ldId = 'ld-product-schema';
    if (!document.getElementById(ldId)) {
      const ld = document.createElement('script');
      ld.id = ldId;
      ld.type = 'application/ld+json';
      ld.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: 'MASTERPACK DE PROTOCOLOS CLÍNICOS DE NUTRICIÓN',
        description: PAGE_DESC,
        image: HERO_IMAGE,
        brand: {
          '@type': 'Brand',
          name: 'Masterpack Nutrición'
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.9',
          reviewCount: '128'
        },
        offers: {
          '@type': 'Offer',
          price: '19990',
          priceCurrency: 'ARS',
          availability: 'https://schema.org/InStock',
          url: `https://masterpack-de-protocolos-clinicos-de-nutricion.impultienda.ar/checkout`
        }
      });
      document.head.appendChild(ld);
    }
    const faqLdId = 'ld-faq-schema';
    if (!document.getElementById(faqLdId)) {
      const faqLd = document.createElement('script');
      faqLd.id = faqLdId;
      faqLd.type = 'application/ld+json';
      faqLd.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [{
          q: '¿Para qué tipo de profesional está recomendado?',
          a: 'Para nutricionistas, licenciados en nutrición, médicos con enfoque nutricional o funcional y profesionales de la salud que necesitan materiales clínicos listos, prolijos y reutilizables.'
        }, {
          q: '¿Es un producto físico o digital?',
          a: 'Es 100% digital. Recibís todos los archivos en PDF de alta resolución directamente en tu email, listos para descargar, imprimir o enviar por WhatsApp.'
        }, {
          q: '¿Puedo ponerle mi logo o firma profesional?',
          a: 'Sí. El material se personaliza con tu identidad profesional y la licencia incluida te permite usarlo con todos tus pacientes sin límites.'
        }, {
          q: '¿Sirve para pacientes de cualquier país?',
          a: 'Sí. Los protocolos aplican ciencia internacional y las listas incluyen alimentos universales adaptables a toda Latinoamérica y España.'
        }, {
          q: '¿Qué pasa si mi paciente tiene una patología no incluida?',
          a: 'Los protocolos base y las herramientas de monitoreo funcionan como soporte para cualquier abordaje nutricional, incluso fuera de las patologías específicas del pack.'
        }].map(f => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: {
            '@type': 'Answer',
            text: f.a
          }
        }))
      });
      document.head.appendChild(faqLd);
    }
  }, []);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    const w = window as any;
    // Standard Meta Pixel base code. Fires immediately on load so the
    // Meta Pixel Helper extension can detect it.
    if (!w.fbq) {
      const n: any = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!w._fbq) w._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [];
      w.fbq = n;
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://connect.facebook.net/en_US/fbevents.js';
      const first = document.getElementsByTagName('script')[0];
      if (first && first.parentNode) {
        first.parentNode.insertBefore(script, first);
      } else {
        ;
        (document.head || document.documentElement).appendChild(script);
      }
    }
    w.fbq('init', META_PIXEL_ID);
    w.fbq('track', 'PageView');
    // <noscript> fallback pixel, as recommended by Meta.
    const nsId = 'fb-pixel-noscript';
    if (!document.getElementById(nsId)) {
      const ns = document.createElement('noscript');
      ns.id = nsId;
      const img = document.createElement('img');
      img.height = 1;
      img.width = 1;
      img.style.display = 'none';
      img.src = `https://www.facebook.com/tr?id=${META_PIXEL_ID}&ev=PageView&noscript=1`;
      ns.appendChild(img);
      document.body.appendChild(ns);
    }
  }, []);
  return <MotionConfig reducedMotion="user" data-id="element-0">
      <Landing data-id="element-1" />
    </MotionConfig>;
}